import React, { useState } from "react";

import {
  Alert,
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";

import {
  CheckCircleRounded,
  CloseRounded,
  RadioButtonUncheckedRounded,
  VisibilityOffRounded,
  VisibilityRounded,
} from "@mui/icons-material";

import { motion } from "framer-motion";
import { styled } from "@mui/system";

import ErrorState from "@componentes/ErrorWindow";
import { api } from "@api/api";

import { botaoCancelar, botaoSalvar } from "../../estilos/botoes";

import { tamanhosModaisGlobais } from "../../estilos/global";

// ====================================================
// ESTILOS DA PÁGINA ANTIGA
// Preservados durante a transição.
// ====================================================

const StyledContainer = styled(Container)({
  backgroundColor: "var(--sear-superficie-suave, #f7f7f7)",
  borderRadius: "16px",
  boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
  padding: "40px 30px",
  transition: "all 0.3s ease",
});

const Title = styled(Typography)({
  textAlign: "center",
  color: "var(--sear-azul, #1976d2)",
  fontWeight: "bold",
  marginBottom: "30px",
});

const FormBox = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "20px",
  padding: "20px 0",
});

const StyledTextField = styled(TextField)({
  "& .MuiInputBase-root": {
    borderRadius: "8px",
  },
  "& .MuiFormLabel-root": {
    fontWeight: "bold",
  },
});

const SubmitButton = styled(Button)({
  background: "var(--sear-acao-azul, #1976d2)",
  borderRadius: "12px",
  padding: "12px 20px",
  "&:hover": {
    background: "var(--sear-acao-azul-hover, #1565c0)",
  },
});

// ====================================================
// ESTILOS ESPECÍFICOS DO MODAL
// Tamanhos das fontes vêm de global.js.
// ====================================================

const estiloCampoModal = {
  "& .MuiOutlinedInput-root": {
    minHeight: 42,
    borderRadius: 1,
    backgroundColor: "var(--sear-superficie, #ffffff)",
    fontSize: tamanhosModaisGlobais.campo,

    "& fieldset": {
      borderColor: "var(--sear-borda, #cbdaf0)",
    },

    "&:hover fieldset": {
      borderColor: "var(--sear-borda, #93b9ed)",
    },

    "&.Mui-focused fieldset": {
      borderColor: "var(--sear-borda-forte, #087cf0)",
    },
  },

  "& .MuiInputBase-input": {
    px: 1.4,
    py: 1.1,
    minWidth: 0,
    fontSize: tamanhosModaisGlobais.campo,
    color: "var(--sear-texto, #314967)",

    "&::placeholder": {
      color: "var(--sear-texto-suave, #8194b2)",
      opacity: 1,
    },
  },
};

const estiloRotuloModal = {
  display: "block",
  mb: 0.55,
  color: "var(--sear-azul, #27456f)",
  fontSize: tamanhosModaisGlobais.rotulo,
  fontWeight: 700,
  lineHeight: 1.35,
};

// ====================================================
// CAMPO REUTILIZADO NO MODAL
// ====================================================

function CampoModal({
  id,
  rotulo,
  nome,
  valor,
  aoAlterar,
  placeholder,
  tipo = "text",
  desabilitado,
  autoComplete = "off",
  endAdornment,
}) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography component="label" htmlFor={id} sx={estiloRotuloModal}>
        {rotulo}
      </Typography>

      <TextField
        id={id}
        name={nome}
        type={tipo}
        value={valor}
        onChange={aoAlterar}
        placeholder={placeholder}
        autoComplete={autoComplete}
        disabled={desabilitado}
        size="small"
        fullWidth
        InputProps={endAdornment ? { endAdornment } : undefined}
        sx={estiloCampoModal}
      />
    </Box>
  );
}

// ====================================================
// CADASTRO DE ANALISTA
// ====================================================

export default function RegisterAnalystSRE({
  emModal = false,
  onCancelar,
  onCadastrado,
} = {}) {
  const [analystData, setAnalystData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
  });

  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [errorType, setErrorType] = useState(null);

  const [salvando, setSalvando] = useState(false);

  const [mostrarSenha, setMostrarSenha] = useState(false);

  const [mostrarConfirmacao, setMostrarConfirmacao] = useState(false);

  // ====================================================
  // REQUISITOS DA SENHA
  // ====================================================

  const senha = analystData.password;

  const requisitosSenha = [
    {
      id: "tamanho",
      texto: "Mínimo de 8 caracteres",
      atendido: senha.length >= 8,
    },
    {
      id: "maiuscula",
      texto: "Pelo menos uma letra maiúscula (A-Z)",
      atendido: /[A-Z]/.test(senha),
    },
    {
      id: "minuscula",
      texto: "Pelo menos uma letra minúscula (a-z)",
      atendido: /[a-z]/.test(senha),
    },
    {
      id: "numero",
      texto: "Pelo menos um número (0-9)",
      atendido: /[0-9]/.test(senha),
    },
    {
      id: "especial",
      texto: "Pelo menos um caractere especial (!@#$%^&*)",
      atendido: /[!@#$%^&*]/.test(senha),
    },
  ];

  const forcaSenha = requisitosSenha.filter(
    (requisito) => requisito.atendido,
  ).length;

  const senhaAtendeRequisitos = requisitosSenha.every(
    (requisito) => requisito.atendido,
  );

  // ====================================================
  // ALTERAÇÃO DOS CAMPOS
  // ====================================================

  const handleChange = ({ target: { name, value } }) => {
    setAnalystData((atual) => ({
      ...atual,
      [name]: value,
    }));

    setError(null);
    setMessage(null);
  };

  // ====================================================
  // CADASTRAR ANALISTA
  // ====================================================

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (salvando) return;

    const { name, email, password, confirmPassword } = analystData;

    if (!name.trim() || !email.trim() || !password || !confirmPassword) {
      setError("Todos os campos são obrigatórios!");
      setMessage(null);
      return;
    }

    if (!senhaAtendeRequisitos) {
      setError("A senha deve atender a todos os requisitos apresentados.");
      setMessage(null);
      return;
    }

    if (password !== confirmPassword) {
      setError("As senhas não coincidem!");
      setMessage(null);
      return;
    }

    setError(null);
    setMessage(null);
    setSalvando(true);

    try {
      const response = await fetch(api.adminRegister(), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          nome: name.trim(),
          email: email.trim(),
          senha: password,
          regra: "analyst",
        }),
      });

      const result = await response.json().catch(() => ({}));

      const cadastroConfirmado =
        response.ok && result.msg === "Usuário cadastrado com sucesso!";

      if (!cadastroConfirmado) {
        setError(result.msg || result.message || "Erro ao cadastrar analista.");
        return;
      }

      setMessage(result.msg);

      setAnalystData({
        name: "",
        email: "",
        password: "",
        confirmPassword: "",
      });

      setMostrarSenha(false);
      setMostrarConfirmacao(false);

      if (typeof onCadastrado === "function") {
        onCadastrado();
      }
    } catch (err) {
      console.error("Erro ao cadastrar analista:", err);

      if (emModal) {
        setError("Não foi possível concluir o cadastro. Tente novamente.");
      } else {
        setErrorType("module");
      }
    } finally {
      setSalvando(false);
    }
  };

  // ====================================================
  // TRATAMENTO DE ERRO DA PÁGINA ANTIGA
  // ====================================================

  if (errorType && !emModal) {
    return (
      <ErrorState errorType={errorType} onRetry={() => setErrorType(null)} />
    );
  }

  // ====================================================
  // MODAL INTEGRADO À LISTA DE USUÁRIOS
  // ====================================================

  if (emModal) {
    return (
      <Dialog
        open
        fullWidth
        maxWidth="sm"
        onClose={salvando ? undefined : onCancelar}
        aria-labelledby="titulo-cadastro-analista"
        PaperProps={{
          sx: {
            width: "calc(100% - 24px)",
            maxWidth: "580px !important",
            maxHeight: "calc(100% - 24px)",
            m: 1,
            borderRadius: 1.7,
            border: "1px solid var(--sear-borda, #d7e7f7)",
            borderTop: "4px solid var(--sear-borda-forte, #087cf0)",
            boxShadow: "0 14px 35px rgba(20, 45, 80, 0.22)",
            overflow: "hidden",
          },
        }}
      >
        {/* CABEÇALHO */}

        <DialogTitle
          id="titulo-cadastro-analista"
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 1.5,
            px: 2.5,
            pt: 2,
            pb: 1.6,
            borderBottom: "1px solid var(--sear-borda, #e5edf5)",
            backgroundColor: "var(--sear-superficie, #ffffff)",
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              component="h2"
              sx={{
                color: "var(--sear-azul, #0767d8)",
                fontSize: tamanhosModaisGlobais.tituloModal,
                fontWeight: 700,
                lineHeight: 1.3,
              }}
            >
              Cadastrar analista
            </Typography>

            <Typography
              sx={{
                mt: 0.35,
                color: "var(--sear-texto-secundario, #71849d)",
                fontSize: tamanhosModaisGlobais.descricao,
                lineHeight: 1.45,
              }}
            >
              Cadastre um novo analista para acesso ao sistema.
            </Typography>
          </Box>

          <IconButton
            size="small"
            aria-label="Fechar cadastro"
            disabled={salvando}
            onClick={onCancelar}
            sx={{
              color: "var(--sear-texto-secundario, #65758c)",
              mt: 0.15,

              "&:hover": {
                color: "var(--sear-azul, #0767d8)",
                backgroundColor: "var(--sear-superficie-azul, #eef6ff)",
              },
            }}
          >
            <CloseRounded sx={{ fontSize: 21 }} />
          </IconButton>
        </DialogTitle>

        {/* FORMULÁRIO */}

        <DialogContent
          sx={{
            px: 2.5,
            py: "20px !important",
            backgroundColor: "var(--sear-superficie, #ffffff)",
          }}
        >
          <Box
            component="form"
            id="formulario-cadastro-analista"
            onSubmit={handleSubmit}
            autoComplete="off"
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1.7,
            }}
          >
            {/* NOME */}

            <CampoModal
              id="cadastro-analista-nome"
              rotulo="Nome"
              nome="name"
              valor={analystData.name}
              aoAlterar={handleChange}
              placeholder="Digite o nome completo do analista..."
              desabilitado={salvando}
            />

            {/* E-MAIL */}

            <CampoModal
              id="cadastro-analista-email"
              rotulo="E-mail institucional"
              nome="email"
              tipo="email"
              valor={analystData.email}
              aoAlterar={handleChange}
              placeholder="exemplo@educacao.mg.gov.br"
              desabilitado={salvando}
            />

            {/* SENHA E CONFIRMAÇÃO */}

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, minmax(0, 1fr))",
                },
                gap: 1.5,
              }}
            >
              <CampoModal
                id="cadastro-analista-senha"
                rotulo="Senha"
                nome="password"
                tipo={mostrarSenha ? "text" : "password"}
                valor={analystData.password}
                aoAlterar={handleChange}
                placeholder="Digite a senha..."
                autoComplete="new-password"
                desabilitado={salvando}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      edge="end"
                      disabled={salvando}
                      onClick={() => setMostrarSenha((atual) => !atual)}
                      aria-label={
                        mostrarSenha ? "Ocultar senha" : "Mostrar senha"
                      }
                      sx={{ color: "var(--sear-texto-secundario, #667b98)" }}
                    >
                      {mostrarSenha ? (
                        <VisibilityRounded sx={{ fontSize: 20 }} />
                      ) : (
                        <VisibilityOffRounded sx={{ fontSize: 20 }} />
                      )}
                    </IconButton>
                  </InputAdornment>
                }
              />

              <CampoModal
                id="cadastro-analista-confirmacao"
                rotulo="Confirmar senha"
                nome="confirmPassword"
                tipo={mostrarConfirmacao ? "text" : "password"}
                valor={analystData.confirmPassword}
                aoAlterar={handleChange}
                placeholder="Confirme a senha..."
                autoComplete="new-password"
                desabilitado={salvando}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      edge="end"
                      disabled={salvando}
                      onClick={() => setMostrarConfirmacao((atual) => !atual)}
                      aria-label={
                        mostrarConfirmacao
                          ? "Ocultar confirmação da senha"
                          : "Mostrar confirmação da senha"
                      }
                      sx={{ color: "var(--sear-texto-secundario, #667b98)" }}
                    >
                      {mostrarConfirmacao ? (
                        <VisibilityRounded sx={{ fontSize: 20 }} />
                      ) : (
                        <VisibilityOffRounded sx={{ fontSize: 20 }} />
                      )}
                    </IconButton>
                  </InputAdornment>
                }
              />
            </Box>

            {/* REQUISITOS DA SENHA */}

            <Box
              sx={{
                px: 1.6,
                py: 1.45,
                borderRadius: 1.2,
                border: "1px solid var(--sear-borda, #e0edfc)",
                background: "linear-gradient(120deg, var(--sear-superficie-azul, #e8f3ff) 0%, var(--sear-superficie-azul, #e0efff) 100%)",
              }}
            >
              <Typography
                sx={{
                  color: "var(--sear-azul, #0767d8)",
                  fontSize: tamanhosModaisGlobais.tituloSecao,
                  fontWeight: 700,
                  mb: 1,
                }}
              >
                Requisitos da senha
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.65,
                }}
              >
                {requisitosSenha.map((requisito) => (
                  <Box
                    key={requisito.id}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 0.9,
                    }}
                  >
                    {requisito.atendido ? (
                      <CheckCircleRounded
                        sx={{
                          color: "var(--sear-azul, #087cf0)",
                          fontSize: 18,
                          flexShrink: 0,
                          mt: 0.1,
                        }}
                      />
                    ) : (
                      <RadioButtonUncheckedRounded
                        sx={{
                          color: "var(--sear-texto-secundario, #59799e)",
                          fontSize: 18,
                          flexShrink: 0,
                          mt: 0.1,
                        }}
                      />
                    )}

                    <Typography
                      sx={{
                        color: requisito.atendido ? "var(--sear-azul, #0767d8)" : "var(--sear-texto-secundario, #435a79)",
                        fontSize: tamanhosModaisGlobais.textoAuxiliar,
                        lineHeight: 1.45,
                      }}
                    >
                      {requisito.texto}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {/* INDICADOR DOS CRITÉRIOS */}

              <Box sx={{ mt: 1.4 }}>
                <Typography
                  sx={{
                    color: "var(--sear-texto-secundario, #435a79)",
                    fontSize: tamanhosModaisGlobais.textoAuxiliar,
                    fontWeight: 700,
                    mb: 0.65,
                  }}
                >
                  Força da senha
                </Typography>

                <Box
                  role="progressbar"
                  aria-label="Indicador de critérios da senha atendidos"
                  aria-valuemin={0}
                  aria-valuemax={5}
                  aria-valuenow={forcaSenha}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
                    gap: 0.6,
                  }}
                >
                  {[1, 2, 3, 4, 5].map((nivel) => (
                    <Box
                      key={nivel}
                      sx={{
                        height: 7,
                        borderRadius: 4,
                        backgroundColor:
                          forcaSenha >= nivel ? "var(--sear-acao-azul, #087cf0)" : "var(--sear-superficie-hover, #ccd8e9)",
                        transition: "background-color 0.2s ease",
                      }}
                    />
                  ))}
                </Box>
              </Box>
            </Box>

            {/* MENSAGENS */}

            {error && (
              <Alert
                severity="error"
                sx={{
                  fontSize: tamanhosModaisGlobais.textoAuxiliar,
                }}
              >
                {error}
              </Alert>
            )}

            {message && (
              <Alert
                severity="success"
                sx={{
                  fontSize: tamanhosModaisGlobais.textoAuxiliar,
                }}
              >
                {message}
              </Alert>
            )}
          </Box>
        </DialogContent>

        {/* RODAPÉ */}

        <DialogActions
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            flexWrap: "wrap",
            gap: 1,
            px: 2.5,
            py: 1.5,
            borderTop: "1px solid var(--sear-borda, #e5edf5)",
            backgroundColor: "var(--sear-superficie, #ffffff)",
          }}
        >
          <Button
            variant="outlined"
            onClick={onCancelar}
            disabled={salvando}
            sx={botaoCancelar}
          >
            Cancelar
          </Button>

          <Button
            variant="outlined"
            type="submit"
            form="formulario-cadastro-analista"
            disabled={salvando}
            sx={botaoSalvar}
          >
            {salvando ? "Cadastrando..." : "Cadastrar analista"}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  // ====================================================
  // PÁGINA ANTIGA
  // Preservada até o Painel da Superintendência.
  // ====================================================

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <StyledContainer maxWidth="sm">
        <Title variant="h4">Registro do Servidor Público</Title>

        {message && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {message}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <FormBox>
            <motion.div
              initial={{ x: "-100vw" }}
              animate={{ x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <StyledTextField
                label="Nome"
                name="name"
                value={analystData.name}
                onChange={handleChange}
                fullWidth
                disabled={salvando}
              />
            </motion.div>

            <motion.div
              initial={{ x: "-100vw" }}
              animate={{ x: 0 }}
              transition={{
                duration: 0.6,
                delay: 0.2,
              }}
            >
              <StyledTextField
                label="E-mail"
                name="email"
                type="email"
                value={analystData.email}
                onChange={handleChange}
                fullWidth
                disabled={salvando}
              />
            </motion.div>

            <motion.div
              initial={{ x: "-100vw" }}
              animate={{ x: 0 }}
              transition={{
                duration: 0.6,
                delay: 0.4,
              }}
            >
              <StyledTextField
                label="Senha"
                name="password"
                type="password"
                value={analystData.password}
                onChange={handleChange}
                fullWidth
                disabled={salvando}
              />
            </motion.div>

            <motion.div
              initial={{ x: "-100vw" }}
              animate={{ x: 0 }}
              transition={{
                duration: 0.6,
                delay: 0.6,
              }}
            >
              <StyledTextField
                label="Confirme a senha"
                name="confirmPassword"
                type="password"
                value={analystData.confirmPassword}
                onChange={handleChange}
                fullWidth
                disabled={salvando}
              />
            </motion.div>
          </FormBox>

          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <SubmitButton
              type="submit"
              variant="contained"
              fullWidth
              disabled={salvando}
            >
              {salvando ? "Registrando..." : "Registrar Servidor"}
            </SubmitButton>
          </motion.div>
        </form>
      </StyledContainer>
    </motion.div>
  );
}
